export const oauthIntegrations = [
  'github',
  'bitbucket',
  'gitlab',
  'azure',
  'azure_devops',
  'azure_devops_legacy',
  'gcp',
] as const;
export const customIntegrations = [
  'github_server',
  'gitlab_server',
  'bitbucket_server',
  'codecommit',
  'beanstalk',
  'aws',
] as const;
export const shareFileIntegrations = ['share_file_https'] as const;
export const providers = [...oauthIntegrations, ...customIntegrations, ...shareFileIntegrations];

export type IStaticIntegration = {
  provider: (typeof providers)[number];
  name: string;
  description: string;
  __typename: 'OauthIntegration' | 'CustomIntegration' | 'ShareFileIntegration';
  assetSupported?: string;
  depositMethod: 'OAuth' | 'Token' | 'Manual';
  nonAuth?: boolean;
  keyLabel?: string;
  passwordLabel?: string;
  accountUrlRequired?: boolean;
};

export const oauthIntegrationsRich: IStaticIntegration[] = [
  {
    provider: 'github',
    name: 'GitHub',
    description:
      'Cloud-based source code management platform that is used to manage open and private Git repositories.',
    assetSupported: 'Git repositories',
    depositMethod: 'OAuth',
    __typename: 'OauthIntegration',
  },
  {
    provider: 'bitbucket',
    name: 'Bitbucket',
    description:
      'Web-based version control repository hosting service for source code and development projects that use either Mercurial or Git revision control systems.',
    assetSupported: 'Git repositories',
    depositMethod: 'OAuth',
    __typename: 'OauthIntegration',
  },
  {
    provider: 'gitlab',
    name: 'GitLab',
    description:
      'Cloud-based source code management platform that is used to manage open and private Git repositories.',
    assetSupported: 'Git repositories',
    depositMethod: 'OAuth',
    __typename: 'OauthIntegration',
  },
  {
    provider: 'azure',
    name: 'Azure',
    description: 'Provides various services related to networking, storage and on-demand cloud computing platforms.',
    assetSupported: 'Blob storage',
    depositMethod: 'OAuth',
    __typename: 'OauthIntegration',
  },
  {
    provider: 'azure_devops',
    name: 'Azure DevOps',
    description:
      'Provided by Microsoft, Azure DevOps is a source code version control system that helps developers track changes made to Git repositories and TFVC over time.',
    assetSupported: 'Git repositories, TFVC',
    depositMethod: 'OAuth',
    __typename: 'OauthIntegration',
  },
  {
    provider: 'gcp',
    name: 'GCP',
    description:
      'Google Cloud Platform, offered by Google, offers a suite of cloud services that includes data storage, and building and deployment of web-based applications.',
    assetSupported: 'Google Cloud Storage',
    depositMethod: 'OAuth',
    __typename: 'OauthIntegration',
  },
];

export const customIntegrationsRich: IStaticIntegration[] = [
  {
    provider: 'github_server',
    name: 'GitHub Enterprise',
    description:
      'Privately hosted platform used for software development and collaboration, including GitHub.com features such as Git version control.',
    assetSupported: 'Git repositories',
    depositMethod: 'Token',
    nonAuth: true,
    keyLabel: 'Username',
    passwordLabel: 'Personal Access Token',
    accountUrlRequired: true,
    __typename: 'CustomIntegration',
  },
  {
    provider: 'gitlab_server',
    name: 'GitLab Self-Managed',
    description:
      'Self-hosted solution that allows organizations to run GitLab on their private network, providing full control over access and data privacy while managing Git repositories.',
    assetSupported: 'Git repositories',
    depositMethod: 'Token',
    nonAuth: true,
    keyLabel: 'Username',
    passwordLabel: 'Personal Access Token',
    accountUrlRequired: true,
    __typename: 'CustomIntegration',
  },
  {
    provider: 'bitbucket_server',
    name: 'Bitbucket Server',
    description: 'Part of the Atlassian suite of solutions, is an on-premises source code management solution for Git.',
    assetSupported: 'Git repositories',
    depositMethod: 'Token',
    nonAuth: true,
    keyLabel: 'Username',
    passwordLabel: 'App Password',
    accountUrlRequired: true,
    __typename: 'CustomIntegration',
  },
  {
    provider: 'codecommit',
    name: 'Amazon CodeCommit',
    description:
      'Secure, scalable, source control service provided by Amazon Web Services, used to host and collaborate on private Git repositories.',
    assetSupported: 'Git repositories',
    depositMethod: 'Token',
    nonAuth: true,
    keyLabel: 'Username',
    passwordLabel: 'Password',
    accountUrlRequired: false,
    __typename: 'CustomIntegration',
  },
  {
    provider: 'beanstalk',
    name: 'Beanstalk',
    description:
      'The complete workflow to write, review and deploy information contained in Git repositories can be managed from within Beanstalk.',
    assetSupported: 'Git repositories',
    depositMethod: 'Token',
    nonAuth: true,
    keyLabel: 'Username',
    passwordLabel: 'Password',
    accountUrlRequired: false,
    __typename: 'CustomIntegration',
  },
  {
    provider: 'aws',
    name: 'Amazon S3',
    description:
      'Global object storage service provided by Amazon Web Services, used to store and protect various types of data and digital information.',
    assetSupported: 'Buckets',
    depositMethod: 'Token',
    nonAuth: true,
    keyLabel: 'Access Key ID',
    passwordLabel: 'Secret Access Key',
    accountUrlRequired: false,
    __typename: 'CustomIntegration',
  },
];

export const staticIntegrations: IStaticIntegration[] = [
  ...oauthIntegrationsRich,
  ...customIntegrationsRich,
  {
    provider: 'share_file_https',
    name: 'Sharefile',
    description:
      'Secure File Transfer Protocol (SFTP) platform used to manually upload your Software and SaaS Escrow assets.',
    depositMethod: 'Manual',
    nonAuth: true,
    assetSupported: 'File uploads',
    __typename: 'ShareFileIntegration',
  },
];

export const providerUrlSlugs: {
  [key in (typeof providers)[number]]: string;
} = {
  github: 'github',
  bitbucket: 'bitbucket',
  gitlab: 'gitlab',
  azure: 'azure',
  azure_devops: 'azure-devops',
  azure_devops_legacy: 'azure-devops',
  gcp: 'google-cloud-platform',
  github_server: 'github-enterprise',
  gitlab_server: 'gitlab-private',
  bitbucket_server: 'bitbucket-server',
  codecommit: 'amazon-codecommit',
  beanstalk: 'beanstalk',
  aws: 'amazon-s3',
  share_file_https: 'sharefile',
};
